import React, { useRef, useState } from "react";
import { Modal, notification } from "antd";
import Button from "../../commons/Button/Button";
import Typography from "../../commons/Typography/Typography";
import { DropdownWithSearch } from "../../commons/Dropdown/DropdownWithSearch";
import { FormField } from "../../commons/FormField/FormField";
import Spinner from "../../commons/Spinner/Spinner";
import useAutomationLanguages from "../../state/hooks/automation/useAutomationLanguages";
import useProcessFile from "../../state/hooks/automation/useProcessFile";
import useJobs, { useInvalidateJobs } from "../../state/hooks/automation/useJobs";
import useDownloadResult from "../../state/hooks/automation/useDownloadResult";
import useCancelJob from "../../state/hooks/automation/useCancelJob";
import useAutomationPrompts from "../../state/hooks/automation/useAutomationPrompts";
import useUpdateAutomationPrompt from "../../state/hooks/automation/useUpdateAutomationPrompt";
import {
  AutomationMode,
  AutomationJobStatus,
  JobListItem,
  AutomationPrompt,
} from "../../models/Interface/Automation.interface";

const MODE_OPTIONS = [
  { value: AutomationMode.SENTENCE, label: "Sentence" },
  { value: AutomationMode.WORD, label: "Word" },
  { value: AutomationMode.DIALOGUE, label: "Dialogue" },
  { value: AutomationMode.WORD_SENTENCE, label: "Word + Sentence" },
  { value: AutomationMode.COURSE_DIALOGUE, label: "Course Dialogue" },
];

const MODE_LABELS: Record<string, string> = {
  sentence: "Sentence",
  word: "Word",
  dialogue: "Dialogue",
  word_sentence: "Word + Sentence",
  course_dialogue: "Course Dialogue",
};

const STATUS_CONFIG: Record<AutomationJobStatus, { bg: string; text: string; label: string }> = {
  [AutomationJobStatus.PENDING]: { bg: "bg-light-grey-3", text: "text-dark-grey", label: "Pending" },
  [AutomationJobStatus.PROCESSING]: { bg: "bg-primary", text: "text-white", label: "Processing" },
  [AutomationJobStatus.DONE]: { bg: "bg-success", text: "text-white", label: "Done" },
  [AutomationJobStatus.ERROR]: { bg: "bg-error", text: "text-white", label: "Error" },
  [AutomationJobStatus.CANCELLED]: { bg: "bg-light-grey-3", text: "text-dark-grey", label: "Cancelled" },
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Automation = () => {
  const [mode, setMode] = useState<AutomationMode | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [translationPrompt, setTranslationPrompt] = useState("");
  const [confirmVisible, setConfirmVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: languages, isLoading: languagesLoading } =
    useAutomationLanguages();

  const { mutate: processFile, isPending: isProcessing } = useProcessFile();
  const { data: jobsData, isLoading: jobsLoading } = useJobs();
  const invalidateJobs = useInvalidateJobs();
  const { mutate: downloadResult } = useDownloadResult();
  const { mutate: cancelJob } = useCancelJob();

  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const [cancellingJobId, setCancellingJobId] = useState<string | null>(null);
  const { data: prompts, isLoading: promptsLoading } = useAutomationPrompts();
  const { mutate: updatePrompt, isPending: isUpdatingPrompt } =
    useUpdateAutomationPrompt();

  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const languageOptions = (languages ?? []).map((lang) => ({
    value: lang.code,
    label: `${lang.name} (${lang.code})`,
  }));

  const canSubmit = mode && language && file && !isProcessing;

  const selectedModeLabel =
    MODE_OPTIONS.find((o) => o.value === mode)?.label ?? mode;
  const selectedLanguageLabel =
    languageOptions.find((o) => o.value === language)?.label ?? language;

  const handleSubmit = () => {
    if (!mode || !language || !file) return;

    processFile(
      {
        file,
        mode,
        language,
        translationPrompt: translationPrompt.trim() || undefined,
      },
      {
        onSuccess: () => {
          notification.success({
            message: "Job Started",
            description: "File uploaded successfully. Processing has begun.",
            placement: "top",
          });
          setFile(null);
          setMode(null);
          setLanguage(null);
          setTranslationPrompt("");
          if (fileInputRef.current) fileInputRef.current.value = "";
          invalidateJobs();
        },
        onError: (error) => {
          const detail =
            (error as any)?.response?.data?.detail ??
            "Failed to start processing.";
          notification.error({
            message: "Upload Failed",
            description:
              typeof detail === "string" ? detail : JSON.stringify(detail),
            placement: "top",
          });
        },
      }
    );
  };

  const handleDownload = (jobId: string) => {
    setDownloadingJobId(jobId);
    downloadResult(jobId, {
      onSuccess: () => setDownloadingJobId(null),
      onError: () => {
        setDownloadingJobId(null);
        notification.error({
          message: "Download Failed",
          description: "Could not download the result file. Please try again.",
          placement: "top",
        });
      },
    });
  };

  const handleCancel = (jobId: string) => {
    setCancellingJobId(jobId);
    cancelJob(jobId, {
      onSuccess: () => {
        setCancellingJobId(null);
        notification.success({
          message: "Job Cancelled",
          description: "The job has been cancelled.",
          placement: "top",
        });
      },
      onError: (error) => {
        setCancellingJobId(null);
        const detail =
          (error as any)?.response?.data?.detail ?? "Failed to cancel the job.";
        notification.error({
          message: "Cancel Failed",
          description: typeof detail === "string" ? detail : JSON.stringify(detail),
          placement: "top",
        });
      },
    });
  };

  const jobs = jobsData?.jobs ?? [];

  return (
    <div className="overflow-y-auto px-30 pt-40 pb-30 bg-light-grey-1 h-full">
      <h2 className="text-dark-grey font-bold text-2xl mb-8">
        Educational Content - Translations
      </h2>
      <p className="text-dark-grey mb-30 text-sm" style={{ opacity: 0.6 }}>
        Upload a CSV file, choose a processing mode and target language, then
        track progress and download the results.
      </p>

      {/* Upload Form */}
      <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-30 mb-30">
        <FormField label="Mode">
          <DropdownWithSearch
            options={MODE_OPTIONS}
            placeholder="Select mode..."
            value={mode ?? undefined}
            onChange={(opt) => setMode((opt?.value as AutomationMode) ?? null)}
            notSearchable
            className="w-full"
            id="automation-mode"
          />
        </FormField>

        <FormField label="Language">
          {languagesLoading ? (
            <div className="flex items-center gap-2">
              <Spinner />
              <span className="text-sm text-dark-grey" style={{ opacity: 0.5 }}>
                Loading languages...
              </span>
            </div>
          ) : (
            <DropdownWithSearch
              options={languageOptions}
              placeholder="Search language..."
              value={language ?? undefined}
              onChange={(opt) => setLanguage(opt?.value ?? null)}
              className="w-full"
              id="automation-language"
            />
          )}
        </FormField>

        <FormField label="CSV File">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-dark-grey
              file:mr-4 file:py-2 file:px-4 file:rounded-md file:border
              file:border-light-grey-3 file:text-sm file:font-semibold
              file:bg-white file:text-dark-grey hover:file:bg-light-grey-1
              file:cursor-pointer cursor-pointer"
            id="automation-file"
          />
        </FormField>

        <FormField label="Prompt (optional)">
          <textarea
            value={translationPrompt}
            onChange={(e) => setTranslationPrompt(e.target.value)}
            placeholder="Custom translation instructions..."
            rows={3}
            className="w-full rounded-md border border-light-grey-3 px-4 py-3 text-sm text-dark-grey
              focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
              resize-y"
            id="automation-prompt"
          />
        </FormField>

        <div className="flex items-center gap-4 mt-20">
          <Button
            type="main"
            onClick={() => setConfirmVisible(true)}
            disabled={!canSubmit}
            isLoading={isProcessing}
          >
            Upload & Process
          </Button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal
        title="Confirm Upload"
        open={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={
          <div className="flex justify-end gap-10 mt-20">
            <Button
              type="secondary"
              border
              onClick={() => setConfirmVisible(false)}
            >
              Cancel
            </Button>
            <Button
              type="main"
              isLoading={isProcessing}
              onClick={() => {
                setConfirmVisible(false);
                handleSubmit();
              }}
            >
              Confirm & Process
            </Button>
          </div>
        }
      >
        <p className="text-sm" style={{ color: "#6b7280", marginBottom: 16 }}>
          Please review the details below before submitting:
        </p>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 8px",
          }}
        >
          <tbody>
            <tr>
              <td
                style={{ width: 120, verticalAlign: "top" }}
                className="font-semibold text-dark-grey text-sm"
              >
                Mode:
              </td>
              <td className="text-sm">{selectedModeLabel}</td>
            </tr>
            <tr>
              <td
                style={{ verticalAlign: "top" }}
                className="font-semibold text-dark-grey text-sm"
              >
                Language:
              </td>
              <td className="text-sm">{selectedLanguageLabel}</td>
            </tr>
            <tr>
              <td
                style={{ verticalAlign: "top" }}
                className="font-semibold text-dark-grey text-sm"
              >
                File:
              </td>
              <td className="text-sm" style={{ wordBreak: "break-word" }}>
                {file?.name ?? "—"}
              </td>
            </tr>
            <tr>
              <td
                style={{ verticalAlign: "top" }}
                className="font-semibold text-dark-grey text-sm"
              >
                Prompt:
              </td>
              <td
                className="text-sm"
                style={{
                  color: "#6b7280",
                  fontStyle: "italic",
                  wordBreak: "break-word",
                }}
              >
                {translationPrompt.trim() || "None"}
              </td>
            </tr>
          </tbody>
        </table>
      </Modal>

      {/* Jobs History */}
      <h3 className="text-dark-grey font-bold text-lg mb-20">Jobs History</h3>

      {jobsLoading ? (
        <div className="flex items-center gap-2">
          <Spinner />
          <span className="text-sm text-dark-grey">Loading jobs...</span>
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-30 text-center">
          <p className="text-sm text-dark-grey" style={{ opacity: 0.5 }}>
            No jobs yet. Upload a file to get started.
          </p>
        </div>
      ) : (
        <div
          className="rounded-xl border-light-grey-3 border-solid border overflow-y-auto"
          style={{ maxHeight: 420 }}
        >
          <table
            className="table-auto w-full bg-white border-collapse text-dark-grey"
            style={{ minWidth: 900 }}
          >
            <thead className="sticky top-0 z-10">
              <tr className="h-48 bg-light-grey-4">
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">File</Typography>
                </th>
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">Mode</Typography>
                </th>
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">Language</Typography>
                </th>
                <th className="pl-20 text-left" style={{ minWidth: 180 }}>
                  <Typography weight="extrabold">Progress</Typography>
                </th>
                <th className="pl-20 text-left" style={{ minWidth: 150 }}>
                  <Typography weight="extrabold">Created</Typography>
                </th>
                <th className="pl-20 pr-20 text-left" style={{ minWidth: 140 }}>
                  <Typography weight="extrabold">Action</Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job: JobListItem) => {
                const st = STATUS_CONFIG[job.status] ?? {
                  bg: "bg-light-grey-3",
                  text: "text-dark-grey",
                  label: job.status ?? "Unknown",
                };
                const langLabel =
                  languages?.find((l) => l.code === job.language)?.name ??
                  job.language;

                return (
                  <tr
                    key={job.job_id}
                    className="border-light-grey-3 border-solid border-t hover:bg-light-grey-1"
                  >
                    <td className="px-20 py-20">
                      <Typography weight="semibold">
                        {job.source_filename ?? "—"}
                      </Typography>
                      <span className="text-sm text-dark-grey" style={{ opacity: 0.5 }}>
                        {job.total_rows} rows
                      </span>
                    </td>
                    <td className="px-20 py-20">
                      <span className="inline-block rounded-full border border-primary text-primary text-sm font-semibold px-10 py-[0.3rem] whitespace-nowrap">
                        {MODE_LABELS[job.mode] ?? job.mode}
                      </span>
                    </td>
                    <td className="px-20 py-20">
                      <Typography weight="semibold">{langLabel}</Typography>
                    </td>
                    <td className="px-20 py-20">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="bg-light-grey-1 rounded-full h-[8px] flex-1">
                          <div
                            className={`h-[8px] rounded-full transition-all duration-300 ${
                              job.status === AutomationJobStatus.DONE
                                ? "bg-success"
                                : job.status === AutomationJobStatus.ERROR
                                ? "bg-error"
                                : "bg-primary"
                            }`}
                            style={{ width: `${Math.min(job.progress, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-dark-grey" style={{ minWidth: 42, textAlign: "right" }}>
                          {job.progress.toFixed(0)}%
                        </span>
                      </div>
                      <span
                        className={`inline-block rounded-full text-sm font-semibold px-10 py-[0.2rem] ${st.bg} ${st.text}`}
                      >
                        {st.label}
                      </span>
                    </td>
                    <td className="px-20 py-20">
                      <Typography weight="semibold">
                        {formatDate(job.created_at)}
                      </Typography>
                    </td>
                    <td className="px-20 py-20" style={{ minWidth: 140 }}>
                      {job.status === AutomationJobStatus.DONE ? (
                        <Button
                          type="main"
                          onClick={() => handleDownload(job.job_id)}
                          isLoading={downloadingJobId === job.job_id}
                          width="full"
                        >
                          Download
                        </Button>
                      ) : job.status === AutomationJobStatus.PROCESSING ||
                        job.status === AutomationJobStatus.PENDING ? (
                        <Button
                          type="secondary"
                          border
                          onClick={() => handleCancel(job.job_id)}
                          isLoading={cancellingJobId === job.job_id}
                          width="full"
                        >
                          Cancel
                        </Button>
                      ) : job.status === AutomationJobStatus.CANCELLED ? (
                        <span className="text-sm text-dark-grey font-semibold" style={{ opacity: 0.4 }}>
                          Cancelled
                        </span>
                      ) : job.error_message ? (
                        <span
                          className="text-error text-sm cursor-pointer font-semibold"
                          title={job.error_message}
                        >
                          View error
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Prompts Section */}
      <h3 className="text-dark-grey font-bold text-lg mb-20 mt-30">
        Prompts
      </h3>

      {promptsLoading ? (
        <div className="flex items-center gap-2">
          <Spinner />
          <span className="text-sm text-dark-grey">Loading prompts...</span>
        </div>
      ) : !prompts || prompts.length === 0 ? (
        <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-30 text-center">
          <p className="text-sm text-dark-grey" style={{ opacity: 0.5 }}>
            No prompts configured.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-10">
          {prompts.map((prompt: AutomationPrompt) => {
            const isEditing = editingSlug === prompt.slug;

            return (
              <div
                key={prompt.slug}
                className="bg-white rounded-xl border border-light-grey-3 border-solid p-20"
              >
                <div className="flex items-center justify-between mb-10">
                  <div>
                    <Typography weight="bold">{prompt.title}</Typography>
                    <span className="text-sm text-dark-grey" style={{ opacity: 0.5 }}>
                      {prompt.slug}
                    </span>
                  </div>
                  {!isEditing ? (
                    <Button
                      type="secondary"
                      border
                      onClick={() => {
                        setEditingSlug(prompt.slug);
                        setEditContent(prompt.content);
                        setEditDescription(prompt.description);
                      }}
                    >
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-10">
                      <Button
                        type="secondary"
                        border
                        onClick={() => setEditingSlug(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="main"
                        isLoading={isUpdatingPrompt}
                        onClick={() => {
                          updatePrompt(
                            { slug: prompt.slug, payload: { content: editContent, description: editDescription } },
                            {
                              onSuccess: () => {
                                setEditingSlug(null);
                                notification.success({
                                  message: "Prompt Updated",
                                  description: `"${prompt.title}" has been saved.`,
                                  placement: "top",
                                });
                              },
                              onError: () => {
                                notification.error({
                                  message: "Update Failed",
                                  description: "Could not save the prompt. Please try again.",
                                  placement: "top",
                                });
                              },
                            }
                          );
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <>
                    <div className="mb-10">
                      <label className="block text-sm font-semibold text-dark-grey mb-4">
                        Description
                      </label>
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Short description of this prompt..."
                        className="w-full rounded-md border border-light-grey-3 px-4 py-2 text-sm text-dark-grey
                          focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <label className="block text-sm font-semibold text-dark-grey mb-4">
                      Content
                    </label>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={12}
                      className="w-full rounded-md border border-light-grey-3 px-4 py-3 text-sm text-dark-grey
                        focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                        resize-y font-mono"
                      style={{ lineHeight: 1.6 }}
                    />
                  </>
                ) : (
                  <>
                    <p className="text-sm text-dark-grey mb-10" style={{ opacity: 0.7 }}>
                      {prompt.description}
                    </p>
                    <pre
                      className="text-sm text-dark-grey bg-light-grey-1 rounded-md p-20 overflow-x-auto"
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        lineHeight: 1.6,
                        maxHeight: 200,
                        overflowY: "auto",
                      }}
                    >
                      {prompt.content}
                    </pre>
                  </>
                )}

                <p className="text-sm text-dark-grey mt-10" style={{ opacity: 0.4 }}>
                  Last updated: {formatDate(prompt.updated_at)}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Automation;
